/**
 * AdminCoachesTab Component
 * 
 * Manages coach profiles including:
 * - Coach listing with details
 * - Add/Edit coach profiles
 * - Coach eligibility settings (Inner Edge, Black Female)
 * - Contact information management
 */

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Briefcase, Plus, Edit, Trash2, Eye, Lock, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const AdminCoachesTab = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCoachDialog, setShowCoachDialog] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [coachForm, setCoachForm] = useState({
    name: '',
    title: '',
    short_bio: '',
    bio: '',
    image_url: '',
    credentials: '',
    specialties: '',
    calendly_link: '',
    inner_edge_eligible: false,
    is_black_female: false,
    personal_email: '',
    volition_email: '',
    phone: ''
  });

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/coaches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoaches(response.data);
    } catch (error) {
      console.error('Error fetching coaches:', error);
      toast.error('Failed to load coaches');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload/coach-photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data?.url;
      if (url) {
        setCoachForm(prev => ({ ...prev, image_url: url }));
        toast.success('Image uploaded');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };


  const handleEditCoach = (coach) => {
    setEditingCoach(coach);
    setCoachForm({
      name: coach.name || '',
      title: coach.title || '',
      short_bio: coach.short_bio || '',
      bio: coach.bio || '',
      image_url: coach.image_url || '',
      credentials: Array.isArray(coach.credentials)
        ? coach.credentials.join(', ')
        : (coach.credentials || ''),
      specialties: Array.isArray(coach.specialties)
        ? coach.specialties.join(', ')
        : (coach.specialties || ''),
      calendly_link: coach.calendly_link || '',
      inner_edge_eligible: coach.inner_edge_eligible || false,
      is_black_female: coach.is_black_female || false,
      personal_email: coach.personal_email || '',
      volition_email: coach.volition_email || '',
      phone: coach.phone || ''
    });
    setShowCoachDialog(true);
  };

  // Normalize a comma/semicolon/pipe-separated string (or array) into an array
  // of trimmed non-empty strings. Used right before POST/PUT so the database
  // never stores a comma-joined string for these fields.
  const toListField = (val) => {
    if (Array.isArray(val)) return val.map(v => String(v).trim()).filter(Boolean);
    if (typeof val !== 'string') return [];
    return val.split(/[,;|]/).map(s => s.trim()).filter(Boolean);
  };

  const handleSaveCoach = async () => {
    if (!coachForm.name) {
      toast.error('Coach name is required');
      return;
    }

    const payload = {
      ...coachForm,
      credentials: toListField(coachForm.credentials),
      specialties: toListField(coachForm.specialties),
    };

    try {
      if (editingCoach) {
        await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, payload);
        toast.success('Coach updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/coaches`, payload);
        toast.success('Coach added successfully');
      }
      setShowCoachDialog(false);
      setEditingCoach(null);
      resetForm();
      fetchCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save coach');
    }
  };

  const handleDeleteCoach = async (coachId) => {
    if (!window.confirm('Are you sure you want to delete this coach?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/coaches/${coachId}`);
      toast.success('Coach deleted');
      fetchCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete coach');
    }
  };

  const resetForm = () => {
    setCoachForm({
      name: '',
      title: '',
      short_bio: '',
      bio: '',
      image_url: '',
      credentials: '',
      specialties: '',
      calendly_link: '',
      inner_edge_eligible: false,
      is_black_female: false,
      personal_email: '',
      volition_email: '',
      phone: ''
    });
  };

  return (
    <>
      <Card className="card-base">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Coach Management ({coaches.length} coaches)
            </CardTitle>
            <Button 
              onClick={() => { 
                setEditingCoach(null); 
                resetForm(); 
                setShowCoachDialog(true); 
              }} 
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Coach
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest mx-auto"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {coaches.map((coach) => (
                <div key={coach.id} className="p-4 border border-sand rounded-xl hover:border-forest/30 transition-colors">
                  <div className="flex items-start gap-3 mb-3">
                    <img 
                      src={coach.image_url || 'https://via.placeholder.com/60'} 
                      alt={coach.name} 
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium text-forest">{coach.name}</h3>
                      <p className="text-sm text-gray-500">{coach.title}</p>
                      <div className="flex gap-1 mt-1">
                        {coach.is_published === false && (
                          <Badge className="bg-gray-100 text-gray-600 text-xs">Unpublished</Badge>
                        )}
                        {coach.is_locked && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">Locked</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{coach.short_bio}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {coach.inner_edge_eligible && (
                      <Badge className="bg-forest/10 text-forest text-xs">Inner Edge</Badge>
                    )}
                    {coach.is_black_female && (
                      <Badge className="bg-terracotta/10 text-terracotta text-xs">Black Female</Badge>
                    )}
                    {coach.active_clients_count > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">{coach.active_clients_count} clients</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-forest hover:bg-forest/90 text-white flex-1"
                      onClick={() => window.location.href = `/admin/coaches/${coach.id}`}
                    >
                      <Eye className="h-3 w-3 mr-1" /> View Profile
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleEditCoach(coach)}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteCoach(coach.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coach Edit Dialog */}
      <Dialog open={showCoachDialog} onOpenChange={setShowCoachDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCoach ? 'Edit Coach' : 'Add New Coach'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={coachForm.name} 
                  onChange={(e) => setCoachForm({...coachForm, name: e.target.value})} 
                  placeholder="Dr. Jane Smith" 
                />
              </div>
              <div>
                <Label>Title</Label>
                <Input 
                  value={coachForm.title} 
                  onChange={(e) => setCoachForm({...coachForm, title: e.target.value})} 
                  placeholder="Executive Coach, PCC" 
                />
              </div>
            </div>
            
            {/* Contact Information Section */}
            <div className="border rounded-lg p-4 bg-amber-50/50">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">Private Contact Information</span>
                <span className="text-xs text-amber-600">(Hidden from member-facing profile)</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Personal Email</Label>
                  <Input 
                    type="email"
                    value={coachForm.personal_email} 
                    onChange={(e) => setCoachForm({...coachForm, personal_email: e.target.value})} 
                    placeholder="personal@email.com" 
                  />
                </div>
                <div>
                  <Label>Volition Email</Label>
                  <Input 
                    type="email"
                    value={coachForm.volition_email} 
                    onChange={(e) => setCoachForm({...coachForm, volition_email: e.target.value})} 
                    placeholder="coach@volitionenterprises.com" 
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label>Phone Number</Label>
                <Input 
                  type="tel"
                  value={coachForm.phone} 
                  onChange={(e) => setCoachForm({...coachForm, phone: e.target.value})} 
                  placeholder="+1 555 123 4567" 
                />
              </div>
            </div>

            <div>
              <Label>Short Bio (for cards)</Label>
              <Textarea 
                value={coachForm.short_bio} 
                onChange={(e) => setCoachForm({...coachForm, short_bio: e.target.value})} 
                placeholder="Brief introduction..." 
                rows={2}
              />
            </div>
            <div>
              <Label>Full Bio</Label>
              <Textarea 
                value={coachForm.bio} 
                onChange={(e) => setCoachForm({...coachForm, bio: e.target.value})} 
                placeholder="Detailed biography..." 
                rows={4}
              />
            </div>
            <div>
              <Label>Coach Image</Label>
              <div className="flex items-start gap-4 mt-1">
                {coachForm.image_url && (
                  <img
                    src={coachForm.image_url}
                    alt="Coach preview"
                    className="w-20 h-20 rounded-xl object-cover border"
                  />
                )}
                <div className="flex-1 space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    data-testid="coach-image-upload-btn"
                  >
                    {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  </Button>
                  <Input
                    value={coachForm.image_url}
                    onChange={(e) => setCoachForm({...coachForm, image_url: e.target.value})}
                    placeholder="Or paste image URL..."
                    className="text-xs"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Credentials</Label>
                <Input 
                  value={coachForm.credentials} 
                  onChange={(e) => setCoachForm({...coachForm, credentials: e.target.value})} 
                  placeholder="PhD, PCC, etc." 
                />
              </div>
              <div>
                <Label>Specialties</Label>
                <Input 
                  value={coachForm.specialties} 
                  onChange={(e) => setCoachForm({...coachForm, specialties: e.target.value})} 
                  placeholder="Leadership, Executive Coaching" 
                />
              </div>
            </div>
            <div>
              <Label>Calendly Link</Label>
              <Input 
                value={coachForm.calendly_link} 
                onChange={(e) => setCoachForm({...coachForm, calendly_link: e.target.value})} 
                placeholder="https://calendly.com/..." 
              />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="inner_edge_eligible"
                  checked={coachForm.inner_edge_eligible}
                  onCheckedChange={(checked) => setCoachForm({...coachForm, inner_edge_eligible: checked})}
                />
                <label htmlFor="inner_edge_eligible" className="text-sm">Inner Edge Eligible</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_black_female"
                  checked={coachForm.is_black_female}
                  onCheckedChange={(checked) => setCoachForm({...coachForm, is_black_female: checked})}
                />
                <label htmlFor="is_black_female" className="text-sm">Black Female Coach</label>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCoachDialog(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleSaveCoach}>
              {editingCoach ? 'Update Coach' : 'Add Coach'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminCoachesTab;
